import { useEffect, useRef, useState } from "react";

// Drives a "typing then deleting" animation across a list of phrases — the
// classic animated-placeholder effect. Used by CapabilityHint to make the
// global-search empty state suggest what you can ask, one phrase at a time.
//
// Timings are per-character; deletion is faster than typing so the loop feels
// snappy rather than laborious. Honors `prefers-reduced-motion`: reduced-motion
// users get the first phrase shown statically with no animation at all.
const TYPE_MS = 55; // delay between characters while typing
const DELETE_MS = 26; // delay between characters while deleting (faster)
const HOLD_MS = 1600; // pause once a phrase is fully typed
const GAP_MS = 420; // pause after a phrase is cleared, before the next one

type Phase = "typing" | "holding" | "deleting" | "gap";

export interface TypewriterState {
  /** The currently visible substring of the active phrase. */
  text: string;
  /** Index of the phrase currently being shown. */
  index: number;
  /** True when animation is suppressed (reduced motion) — text is static. */
  reducedMotion: boolean;
}

function readReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(readReducedMotion);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

// `phrases` should be a stable reference (define it as a module constant or
// memoize it) — the animation loop reads the latest value via a ref but only
// (re)starts when the reduced-motion preference changes.
export function useTypewriter(phrases: string[]): TypewriterState {
  const reducedMotion = useReducedMotion();
  const [text, setText] = useState(() => (reducedMotion ? (phrases[0] ?? "") : ""));
  const [index, setIndex] = useState(0);

  const phrasesRef = useRef(phrases);
  phrasesRef.current = phrases;

  useEffect(() => {
    const list = phrasesRef.current;
    if (list.length === 0) {
      setText("");
      return;
    }

    if (reducedMotion) {
      // No animation: show the first phrase and stop.
      setIndex(0);
      setText(list[0] ?? "");
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    let phase: Phase = "typing";
    let phraseIdx = 0;
    let charCount = 0;

    setIndex(0);
    setText("");

    const tick = () => {
      const current = phrasesRef.current[phraseIdx] ?? "";

      switch (phase) {
        case "typing":
          charCount += 1;
          setText(current.slice(0, charCount));
          if (charCount >= current.length) {
            phase = "holding";
            timer = setTimeout(tick, HOLD_MS);
          } else {
            timer = setTimeout(tick, TYPE_MS);
          }
          break;

        case "holding":
          phase = "deleting";
          timer = setTimeout(tick, DELETE_MS);
          break;

        case "deleting":
          charCount -= 1;
          setText(current.slice(0, Math.max(0, charCount)));
          if (charCount <= 0) {
            phase = "gap";
            timer = setTimeout(tick, GAP_MS);
          } else {
            timer = setTimeout(tick, DELETE_MS);
          }
          break;

        case "gap":
          phraseIdx = (phraseIdx + 1) % phrasesRef.current.length;
          charCount = 0;
          phase = "typing";
          setIndex(phraseIdx);
          timer = setTimeout(tick, TYPE_MS);
          break;
      }
    };

    timer = setTimeout(tick, TYPE_MS);
    return () => clearTimeout(timer);
  }, [reducedMotion]);

  return { text, index, reducedMotion };
}
